import { useEffect, useMemo, useRef } from 'react';
import { Box } from '@mui/material';
import * as echarts from 'echarts';
import { InvestmentRentalPeriodDTO } from '@inversor-hub/types';

const eurFormatter = new Intl.NumberFormat('es-ES', {
  style: 'currency',
  currency: 'EUR',
});

const eurAxisFormatter = new Intl.NumberFormat('es-ES', {
  maximumFractionDigits: 0,
  style: 'currency',
  currency: 'EUR',
});

const monthIndex = (date: Date) => date.getFullYear() * 12 + date.getMonth();

/**
 * Builds a month-by-month series where each point is the sum of the monthly
 * rent of every period active during that month. Periods that overlap in the
 * same months (e.g. several rooms rented at once) are aggregated instead of
 * overwriting each other.
 */
function buildMonthlySeries(periods: InvestmentRentalPeriodDTO[]): [string, number][] {
  if (periods.length === 0) return [];

  const currentIndex = monthIndex(new Date());

  const parsed = periods.map((period) => {
    const startIndex = monthIndex(new Date(period.startDate));
    const endIndex = period.endDate ? monthIndex(new Date(period.endDate)) : currentIndex;
    return {
      startIndex,
      // An ongoing or future-ending period contributes at least up to today.
      endIndex: Math.max(startIndex, endIndex),
      rent: period.monthlyRent,
    };
  });

  const minIndex = Math.min(...parsed.map((period) => period.startIndex));
  const maxIndex = Math.max(...parsed.map((period) => period.endIndex));

  const series: [string, number][] = [];
  for (let index = minIndex; index <= maxIndex; index += 1) {
    const total = parsed.reduce(
      (sum, period) =>
        index >= period.startIndex && index <= period.endIndex ? sum + period.rent : sum,
      0
    );
    const label = new Date(Math.floor(index / 12), index % 12, 1).toLocaleDateString('es-ES', {
      month: 'short',
      year: '2-digit',
    });
    series.push([label, total / 100]);
  }

  return series;
}

export function RentalHistoryChart({ periods }: { periods: InvestmentRentalPeriodDTO[] }) {
  const chartRef = useRef<HTMLDivElement | null>(null);

  const data = useMemo(() => buildMonthlySeries(periods), [periods]);

  useEffect(() => {
    if (!chartRef.current || data.length === 0) return;

    const chart = echarts.init(chartRef.current);
    chart.setOption({
      backgroundColor: 'transparent',
      grid: { left: 48, right: 24, top: 24, bottom: 36 },
      tooltip: {
        trigger: 'axis',
        valueFormatter: (value: number) => eurFormatter.format(value),
      },
      xAxis: {
        type: 'category',
        data: data.map(([label]) => label),
        axisTick: { alignWithLabel: true },
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          formatter: (value: number) => eurAxisFormatter.format(value),
        },
      },
      series: [
        {
          name: 'Renta mensual',
          type: 'line',
          step: 'end',
          smooth: false,
          symbolSize: 8,
          lineStyle: { width: 3 },
          areaStyle: { opacity: 0.08 },
          data: data.map(([, value]) => value),
        },
      ],
    });

    const handleResize = () => chart.resize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      chart.dispose();
    };
  }, [data]);

  return <Box ref={chartRef} sx={{ width: '100%', height: 280 }} />;
}
